import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CAMPAIGNS_STATUSES, CAMPAIGNS_TYPES } from 'const';
import { ICampaign } from 'interfaces/campaigns';
import { getAdvertisingCampaigns } from 'services/advertisingCampaigns';
import { useSelector } from 'store';
import {
	selectCompanyProduct,
	selectCompanyProductSubclientId,
	selectCompanyProductSubclientLogin,
} from 'store/companiesReducer';
import { selectActiveProduct } from 'store/productsReducer';
import { isDayPassed } from 'utils/date';
import Button from 'components/Button';
import Card from 'components/Card';
import StatusLabel from 'components/StatusLabel';
import classes from './advertisingCampaignsList.module.scss';

const ADVERTISING_TYPE_LABEL = {
	[CAMPAIGNS_TYPES.SUBSCRIPTION]: 'Рекламная подписка',
	[CAMPAIGNS_TYPES.GEO]: 'Приоритетное размещение в Картах и Навигаторе',
	[CAMPAIGNS_TYPES.WEB]: 'Реклама сайта или социальной сети',
};

const AdvertisingCampaignsList: React.FC = () => {
	const navigate = useNavigate();
	const activeProduct = useSelector(selectActiveProduct);
	const companyPartner = useSelector(selectCompanyProduct);
	const companySubclientId = useSelector(selectCompanyProductSubclientId);
	const companySubclientLogin = useSelector(
		selectCompanyProductSubclientLogin
	);
	const [advertisingCampaigns, setAdvertisingCampaigns] = useState<
		ICampaign[]
	>([]);
	const [isLoading, setIsLoading] = useState(true);

	const getAdvertisingCampaignsData = async (): Promise<void> => {
		setIsLoading(true);
		try {
			const newAdvertisingCampaignsData = await getAdvertisingCampaigns(
				activeProduct?.slug || '',
				companySubclientId
			);
			const newAdvertisingCampaigns =
				newAdvertisingCampaignsData.data.items;
			setAdvertisingCampaigns(newAdvertisingCampaigns);
		} catch (err) {
			console.log(err);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		if (activeProduct && companySubclientId) {
			getAdvertisingCampaignsData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeProduct, companySubclientId]);

	useEffect(() => {
		if (!isLoading && advertisingCampaigns?.length < 1) {
			return navigate('./create');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	const handleCampaignClick =
		(campaignId: number, isNew: boolean) => (): void => {
			let url = `./${campaignId}`;
			if (isNew) {
				url = `./${campaignId}/payment`;
			}
			navigate(url);
		};

	const notStartedCampaigns = advertisingCampaigns.filter(
		(campaign) =>
			campaign?.campaign_partner_info?.status ===
			CAMPAIGNS_STATUSES.WAITING
	);
	const activeCampaigns = advertisingCampaigns.filter((campaign) =>
		[
			CAMPAIGNS_STATUSES.RUNNING,
			CAMPAIGNS_STATUSES.DELAYED_START,
			CAMPAIGNS_STATUSES.PAUSED,
		].includes(campaign?.campaign_partner_info?.status)
	);
	const archiveCampaigns = advertisingCampaigns.filter((campaign) =>
		[CAMPAIGNS_STATUSES.FINISHED, CAMPAIGNS_STATUSES.STOPPED].includes(
			campaign?.campaign_partner_info?.status
		)
	);
	const campaignsData = {
		[CAMPAIGNS_STATUSES.DELAYED_START]: {
			title: 'Незапущенные рекламные кампании',
			getSubtitle: (days: string): string =>
				days ? ` (${days} дней)` : '',
			items: notStartedCampaigns,
		},
		[CAMPAIGNS_STATUSES.RUNNING]: {
			title: 'Активные рекламные кампании',
			// getSubtitle: (days: number): string => `Истекает ${getExpireDate(days)}`,
			items: activeCampaigns,
		},
		[CAMPAIGNS_STATUSES.FINISHED]: {
			title: 'Архивные рекламные кампании',
			items: archiveCampaigns,
		},
	};

	const renderCampaigns = (type: string) => {
		const { title, getSubtitle, items } = campaignsData[type];
		return (
			(isLoading || items.length > 0) && (
				<Card
					className={classes.list}
					key={type}
					title={title}
					isLoading={isLoading}
					withDropDown
				>
					{items.map((item) => (
						<div
							className={classes.item}
							key={item?.id}
							onClick={handleCampaignClick(
								item?.id,
								item?.state === 'new'
							)}
						>
							<div className={classes.itemLogo}>
								{item?.name[0]}
							</div>
							<div className={classes.itemInfo}>
								<div className={classes.itemTitle}>
									{item?.name} №{item?.meta?.id}
									{!isDayPassed(item?.created_at || '') && (
										<span className={classes.itemLabelNew}>
											(Новая)
										</span>
									)}
								</div>
								{item?.meta?.type === 'WEB' &&
									item?.meta?.url && (
										<div className={classes.itemUrl}>
											{item?.meta?.url}
										</div>
									)}
								{item?.meta?.type !== 'WEB' &&
									companyPartner?.name && (
										<div
											className={classes.itemCompanyName}
										>
											{companyPartner?.name}
										</div>
									)}
								<div className={classes.itemType}>
									{ADVERTISING_TYPE_LABEL[item?.meta?.type]}
									{getSubtitle && (
										<span className={classes.itemSubtitle}>
											{getSubtitle(item?.meta?.duration)}
										</span>
									)}
								</div>
							</div>
							<StatusLabel
								className={classes.itemLabel}
								status={
									item?.campaign_partner_info?.status || ''
								}
								isPaid={item?.state !== 'new'}
							/>
						</div>
					))}
				</Card>
			)
		);
	};

	const header = (
		<div className={classes.header}>
			<div className={classes.title}>
				{activeProduct?.name || ''}
				<div className={classes.subtitle}>
					<div className={classes.login}>
						Логин{' '}
						<a
							href="https://business.yandex.ru/"
							target="_blank"
							rel="noreferrer"
							className={classes.link}
						>
							{activeProduct?.name || ''}
						</a>
						: {companySubclientLogin}
					</div>
					{/* TODO: uncomment when balance implemented */}
					{/* <div className={classes.balance}>Баланс: 1000</div> */}
				</div>
			</div>
			<Button
				className={classes.buttonCreate}
				text="Создать новую"
				href="./create"
			/>
		</div>
	);

	return (
		<div className={classes.advertisingCampaignsList}>
			{header}
			{/* TODO: add skeleton loader */}
			{Object.keys(campaignsData).map(renderCampaigns)}
		</div>
	);
};

export default AdvertisingCampaignsList;
